import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { deleteSlashTrigger, type SlashMenuItem } from './slash-menu-items'
import { useSlashMenuStyle } from './useSlashMenuStyle'
import { useMessages } from '@/i18n'
import { messages } from './messages'

export interface SlashMenuPosition {
  left: number
  cursorTop: number
  cursorBottom: number
}

interface Props {
  editor: Editor
  position: SlashMenuPosition | null
  filter: string
  items: SlashMenuItem[]
  onClose: () => void
}

export const SlashMenu = ({ editor, position, filter, items, onClose }: Props) => {
  const m = useMessages(messages)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)

  const filtered = items.filter(
    (item) =>
      item.title.toLowerCase().includes(filter.toLowerCase()) ||
      item.description.toLowerCase().includes(filter.toLowerCase()),
  )

  useEffect(() => setSelectedIndex(0), [filter])

  useEffect(() => {
    if (!position) return
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((i) => (i + 1) % filtered.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const item = filtered[selectedIndex]
        if (item) {
          deleteSlashTrigger(editor)
          item.command(editor)
          onClose()
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [position, filtered, selectedIndex, editor, onClose])

  const resolvedStyle = useSlashMenuStyle(menuRef, position, filtered.length)

  if (!position) return null

  const style = resolvedStyle ?? {
    top: position.cursorBottom + 4,
    left: position.left,
    maxHeight: 320,
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-popover border border-border rounded-xl shadow-lg p-1.5 w-64 overflow-auto"
      style={{ top: style.top, left: style.left, maxHeight: style.maxHeight }}
    >
      {filtered.length === 0 ? (
        <div className="px-3 py-2 text-sm text-muted-foreground">{m.nothingFound}</div>
      ) : (
        filtered.map((item, index) => (
          <button
            key={item.title}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
              index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
            )}
            onMouseEnter={() => setSelectedIndex(index)}
            onClick={() => {
              deleteSlashTrigger(editor)
              item.command(editor)
              onClose()
            }}
          >
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
              {item.icon}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{item.title}</p>
              <p className="text-xs text-muted-foreground truncate">{item.description}</p>
            </div>
          </button>
        ))
      )}
    </div>
  )
}
